import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { LocalStorageKeys } from '@pkg/types';
import { removeLocalStorageItems, setLocalStorageItems } from '@pkg/utils';

import { useAuthSidebar, useAuthTokens, UserType, useUser } from '@app/store/zustand';

import { type AuthenticateOptions, AuthRedirectType } from './types';

export function useAuthentication() {
    const navigate = useNavigate();

    const { isAuthSidebarOpen, closeAuthSidebar, openAuthSidebar } = useAuthSidebar();
    const { isAuthenticated } = useAuthTokens();
    const { type: userType } = useUser();

    const [authCallback, setAuthCallback] = useState<null | (() => void)>(null);

    function protectedAction(protectedAction: () => void) {
        if (!isAuthenticated) {
            setAuthCallback(() => protectedAction);
            openAuthSidebar();
        } else {
            protectedAction();
        }
    }

    function authenticate(options?: AuthenticateOptions) {
        if (options?.redirect) {
            const redirectSetup: { key: string; value: string }[] = [
                {
                    key: LocalStorageKeys.AuthRedirectType,
                    value: options.redirect.type,
                },
            ];
            if (options.redirect.type === AuthRedirectType.Path && options.redirect.path) {
                redirectSetup.push({
                    key: LocalStorageKeys.AuthRedirectPath,
                    value: options.redirect.path,
                });
            }
            setLocalStorageItems(redirectSetup);
        } else {
            // Remove any left over redirect setup in local storage just in case
            removeLocalStorageItems([LocalStorageKeys.AuthRedirectType, LocalStorageKeys.AuthRedirectType]);
        }

        openAuthSidebar();
    }

    /* Handle authentication via onClick event */
    function handleAuthenticate(event?: React.MouseEvent, options?: AuthenticateOptions) {
        event?.preventDefault();
        authenticate(options);
    }

    function protectedRedirect(link: string) {
        if (isAuthenticated) {
            navigate(link);
            return;
        }

        protectedAction(() => navigate(link));
    }

    useEffect(() => {
        if (isAuthenticated) {
            // User is verified, we can close the auth sidebar
            // TODO: Make sure no redirect is setup before closing the sidebar (should this be handled by component calling useAuthentication?)
            if (isAuthSidebarOpen && userType === UserType.Verified) {
                closeAuthSidebar();
            }

            if (authCallback) {
                authCallback();
                setAuthCallback(null);
            }
        }
    }, [authCallback, closeAuthSidebar, isAuthenticated, isAuthSidebarOpen, userType]);

    return {
        authenticate,
        handleAuthenticate,
        protectedAction,
        protectedRedirect,
    };
}
